/**
 * Glossaire bilingue centralisé — Source de vérité pour tous les termes anglo-saxons.
 * Chaque terme expose : nom anglais, traduction française, définition.
 */

export interface GlossaryEntry {
  en: string;
  fr: string;
  definition: string;
}

export const GLOSSARY: Record<string, GlossaryEntry> = {
  'growth-dashboard': {
    en: 'Growth Dashboard',
    fr: 'Tableau de bord croissance',
    definition:
      "Vue d'ensemble des indicateurs de croissance de la plateforme : utilisateurs, organisations, feedbacks, projets.",
  },
  'marketing-dashboard': {
    en: 'Marketing Dashboard',
    fr: 'Tableau de bord marketing',
    definition:
      'Pilotage des performances marketing : contenu, pipeline de prospects, parrainage et KPIs associes.',
  },
  'content-factory': {
    en: 'Content Factory',
    fr: 'Usine a contenu',
    definition:
      "Module de generation automatique de contenus marketing (articles LinkedIn, emails, threads) via l'IA.",
  },
  'sales-intelligence': {
    en: 'Sales Intelligence',
    fr: 'Intelligence commerciale',
    definition:
      "Outils d'aide a la vente : fiches argumentaires (battle cards), profils prospects, emails de prospection.",
  },
  'knowledge-explorer': {
    en: 'Knowledge Explorer',
    fr: 'Explorateur de connaissances',
    definition:
      "Interface de consultation et gestion de la base de connaissances (Knowledge Base) utilisee par l'IA.",
  },
  'content-calendar': {
    en: 'Content Calendar',
    fr: 'Calendrier editorial',
    definition:
      'Planification et suivi des publications marketing dans le temps.',
  },
  'blog-manager': {
    en: 'Blog Manager',
    fr: 'Gestionnaire de blog',
    definition:
      'Creation et gestion des articles de blog SEO avec generation IA et auto-traduction.',
  },
  campaigns: {
    en: 'Campaigns',
    fr: 'Campagnes',
    definition:
      'Suivi des campagnes marketing avec liens vers les contenus et metriques de performance.',
  },
  'referral-network': {
    en: 'Referral Network',
    fr: 'Reseau de parrainage',
    definition:
      'Systeme de parrainage avec ambassadeurs, recompenses et detection anti-fraude.',
  },
  'battle-cards': {
    en: 'Battle Cards',
    fr: 'Fiches argumentaires',
    definition:
      "Documents comparatifs generes par l'IA pour positionner le produit face a la concurrence.",
  },
  'cold-outreach': {
    en: 'Cold Outreach',
    fr: 'Prospection a froid',
    definition:
      'Emails de premier contact envoyes a des prospects qui ne connaissent pas encore le produit.',
  },
  'prospect-pipeline': {
    en: 'Prospect Pipeline',
    fr: 'Tunnel de prospection',
    definition:
      'Suivi des prospects a travers les etapes : nouveau, contacte, relance, en negociation, converti.',
  },
  mrr: {
    en: 'MRR (Monthly Recurring Revenue)',
    fr: 'Revenu mensuel recurrent',
    definition:
      "Somme des abonnements mensuels actifs. Indicateur cle de la sante financiere SaaS.",
  },
  arr: {
    en: 'ARR (Annual Recurring Revenue)',
    fr: 'Revenu annuel recurrent',
    definition:
      'MRR x 12. Projection annuelle du revenu recurrent.',
  },
  churn: {
    en: 'Churn Rate',
    fr: "Taux d'attrition",
    definition:
      "Pourcentage de clients qui se desabonnent sur une periode donnee.",
  },
  byok: {
    en: 'BYOK (Bring Your Own Key)',
    fr: 'Apportez votre propre cle',
    definition:
      "Fonctionnalite Enterprise permettant au client d'utiliser sa propre cle API IA au lieu de celle de la plateforme.",
  },
  token: {
    en: 'AI Tokens',
    fr: 'Jetons IA',
    definition:
      "Unite de consommation de l'IA. Chaque analyse, generation ou transcription consomme des tokens.",
  },
  seo: {
    en: 'SEO (Search Engine Optimization)',
    fr: 'Referencement naturel',
    definition:
      "Techniques pour ameliorer le classement d'un site dans les resultats des moteurs de recherche.",
  },
  slug: {
    en: 'Slug',
    fr: 'Identifiant URL',
    definition:
      "Portion de l'URL lisible par l'humain, generee automatiquement depuis le titre (ex: mon-article-seo).",
  },
  excerpt: {
    en: 'Excerpt',
    fr: 'Extrait / Resume',
    definition:
      "Resume court d'un article (2-3 phrases) affiche dans les listes et les resultats de recherche.",
  },
  'featured-image': {
    en: 'Featured Image',
    fr: 'Image a la une',
    definition:
      "Image principale d'un article, affichee en tete de page et dans les cards de liste.",
  },
  rag: {
    en: 'RAG (Retrieval-Augmented Generation)',
    fr: 'Generation augmentee par la recherche',
    definition:
      "Technique IA qui enrichit le contexte du modele avec des documents pertinents avant de generer une reponse.",
  },
  milestone: {
    en: 'Milestone',
    fr: 'Jalon',
    definition:
      "Point de repere dans une feuille de route, marquant l'achevement d'une etape importante.",
  },
  roi: {
    en: 'ROI (Return On Investment)',
    fr: 'Retour sur investissement',
    definition:
      "Rapport entre le benefice obtenu et le cout de l'investissement.",
  },
  kpi: {
    en: 'KPI (Key Performance Indicator)',
    fr: 'Indicateur cle de performance',
    definition:
      'Metrique mesurable utilisee pour evaluer le succes par rapport a un objectif.',
  },
  pipeline: {
    en: 'Pipeline',
    fr: 'Tunnel de conversion',
    definition:
      'Ensemble des etapes par lesquelles passe un prospect avant de devenir client.',
  },
  'feedback-inbox': {
    en: 'Feedback Inbox',
    fr: 'Boite de reception des feedbacks',
    definition:
      "Centralisation de tous les retours utilisateurs avec analyse IA automatique (sentiment, priorite).",
  },
  'user-list': {
    en: 'User Directory',
    fr: 'Annuaire clients',
    definition:
      'Liste des utilisateurs de la plateforme avec leurs organisations et activites.',
  },
  'ops-dashboard': {
    en: 'Ops Dashboard',
    fr: 'Tableau de bord operations',
    definition:
      'Vue centralisee des metriques operationnelles : organisations, abonnements, quotas.',
  },
  organizations: {
    en: 'Organizations',
    fr: 'Organisations',
    definition:
      "Entites clientes de la plateforme, chacune avec ses utilisateurs et son abonnement.",
  },
  subscriptions: {
    en: 'Subscriptions',
    fr: 'Abonnements',
    definition:
      'Gestion des plans tarifaires, MRR, et suivi Stripe des abonnements clients.',
  },
  'token-watcher': {
    en: 'Token Watcher',
    fr: 'Surveillance des jetons',
    definition:
      "Monitoring en temps reel de la consommation de tokens IA par organisation.",
  },
  'byok-manager': {
    en: 'BYOK Manager',
    fr: 'Gestionnaire de cles',
    definition:
      "Interface de configuration des cles API propres aux clients Enterprise.",
  },
  'tech-dashboard': {
    en: 'Tech Dashboard',
    fr: 'Tableau de bord technique',
    definition:
      "Metriques d'infrastructure : sante des services, latences API, erreurs.",
  },
  roadmap: {
    en: 'Roadmap',
    fr: 'Feuille de route',
    definition:
      'Outil interne de planification des strategies marketing, lancement et operations, correle aux modules Growth.',
  },
} as const;

/**
 * Mapping des clés de navigation sidebar vers les clés du glossaire.
 */
export const SIDEBAR_GLOSSARY_MAP: Record<string, string> = {
  'growth-dashboard': 'growth-dashboard',
  'marketing-dashboard': 'marketing-dashboard',
  'content-factory': 'content-factory',
  'sales-intelligence': 'sales-intelligence',
  'knowledge-explorer': 'knowledge-explorer',
  'content-calendar': 'content-calendar',
  'blog-manager': 'blog-manager',
  campaigns: 'campaigns',
  'referral-dashboard': 'referral-network',
  'feedback-inbox': 'feedback-inbox',
  users: 'user-list',
  'ops-dashboard': 'ops-dashboard',
  organizations: 'organizations',
  subscriptions: 'subscriptions',
  'token-watcher': 'token-watcher',
  'tech-dashboard': 'tech-dashboard',
  roadmap: 'roadmap',
};

export function getGlossaryEntry(key: string): GlossaryEntry | undefined {
  return GLOSSARY[key];
}
